import { AddCircle, Clear, Fullscreen, ToggleOff, ToggleOn } from '@mui/icons-material'
import { Box } from '@mui/material'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import { WorkWave } from '@workwave-tidal/core/icons/logos'
import {
  Conversation,
  ConversationEditor,
  ConversationHeader,
  ConversationHeaderActions,
  ConversationHeaderContent,
  ConversationInteractions,
  ConversationSuggestion,
  ConversationSuggestions,
  FloatingConversation,
  useChatOpen,
  useFloatingConversationApi,
  useFullscreen,
} from '@workwave-tidal/lab/Conversation'
import { useEffect, useState } from 'react'

import { Interaction } from './components/Interaction'
import { useConversation } from './hooks/useConversation'
import { useSuggestions } from './hooks/useSuggestions'

export function RoutingAssistantChatbot() {
  return (
    <Box position="fixed" zIndex={1000} bottom={0} right={0}>
      <FloatingConversation
        slotProps={{
          fab: {
            draggable: true,
          },
        }}
      >
        <ConversationComponent />
      </FloatingConversation>
    </Box>
  )
}

function ConversationComponent() {
  const { conversation, clearConversation, sendMessage } = useConversation()
  const { setChatOpen, setFullScreen } = useFloatingConversationApi()
  const fullScreen = useFullscreen()
  const chatOpen = useChatOpen()

  const [autoReadFlowDisabled, setAutoReadFlowDisabled] = useState(false)

  const suggestions = useSuggestions()

  // WARNING: This is a hack to focus the editor when the chat is opened
  useEffect(() => {
    if (!chatOpen) return
    //@ts-expect-error - This is a hack because autoFocus is not working as expected
    document.querySelector('.TidalEditor-contentEditable')?.focus()
  }, [chatOpen])

  const disableAutoReadFlowMessage = autoReadFlowDisabled
    ? 'Enable auto scrolling'
    : 'Disable auto scrolling'

  return (
    <Conversation scrollToEndAfterMessageSend disableAutoReadFlow={autoReadFlowDisabled}>
      <ConversationHeader
        slotProps={{
          actionBar: {
            size: 'medium',
            limit: 10,
          },
        }}
      >
        <ConversationHeaderContent>
          <WorkWave sx={{ maxWidth: 180 }} />
        </ConversationHeaderContent>
        <ConversationHeaderActions>
          <ActionBarItem
            mode="default"
            icon={<AddCircle />}
            label="New chat"
            tooltip={'Start a new chat'}
            onClick={clearConversation}
          />
          <ActionBarItem
            mode="end"
            icon={<Clear />}
            label="Close chat"
            tooltip={'Close chat'}
            onClick={_event => {
              setChatOpen(false)
            }}
          />
          <ActionBarItem
            mode="default"
            label={fullScreen ? 'Exit full screen' : 'Full screen'}
            tooltip={fullScreen ? 'Exit full screen' : 'Full screen'}
            icon={<Fullscreen />}
            onClick={_event => {
              setFullScreen(!fullScreen)
            }}
          />
          <ActionBarItem
            mode="default"
            icon={autoReadFlowDisabled ? <ToggleOff color="error" /> : <ToggleOn color="success" />}
            tooltip={disableAutoReadFlowMessage}
            label={disableAutoReadFlowMessage}
            onClick={_event => {
              setAutoReadFlowDisabled(prev => !prev)
            }}
          />
        </ConversationHeaderActions>
      </ConversationHeader>

      <ConversationInteractions>
        {/* This is hack required to keep the auto-scroll and the scrolling fab working */}
        <Box height={1} width="100%" />

        {conversation?.historyItems.map((item, index) => {
          const isBot =
            (item.type === 'message' && item.role === 'assistant') ||
            item.type === 'function_call' ||
            item.type === 'function_call_output'

          const loading =
            index === conversation.historyItems.length - 1 && conversation.isProcessing && !isBot

          return <Interaction loading={loading} item={item} key={`history-item-${index}`} />
        })}

        {
          conversation?.pendingMessages.map(item => (
            <Interaction
              loading
              item={{ ...item, role: 'user' }}
              key={`pending-message-${item.id}`}
            />
          )) // Pending messages are only user messages, so no need to check for role
        }
      </ConversationInteractions>

      <ConversationEditor autoFocus onSend={sendMessage}>
        <ConversationSuggestions>
          {suggestions.map(({ label, suggestion }) => (
            <ConversationSuggestion
              key={suggestion}
              label={label ?? suggestion}
              suggestion={suggestion}
            />
          ))}
        </ConversationSuggestions>
      </ConversationEditor>
    </Conversation>
  )
}
